import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
﻿import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  Calendar, Search, Plus, Edit, Trash2, RefreshCcw, CheckCircle, AlertTriangle, XCircle, 
  ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, 
  LayoutGrid, Layers, Users, User, Clock, TrendingUp, FileText, List, LayoutList, Shield, AlertCircle
} from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import localApiAdapter from '../../core/services/localApiAdapter';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import {
  usePointageModalBodyLock,
  getPointageModalFormClasses,
  buildPointageBulkDeleteFilterAction,
  POINTAGE_ACTIONS_TH_CLASS,
  buildPointageColumnVisibilityState,
  buildPointageQawaemColumnDefs,
  isPointageColumnVisible,
  createPointageHandleToggleColumn,
  pointageTableColSpan,
  PointageCrudNotificationToast,
  useAppNotificationToast,
  notifyBulkDeleted,
  notifyCreated,
  notifyDeleted,
  notifyError,
  notifyUpdated,
  sortRecordsNewestFirst,
  buildPointageFormTabs,
  pointageFormTabVisible,
  usePointageEmployeeOptions,
  resolvePointageEmployeeFromSelect,
  resolvePointageEmployeeDisplayName,
  resolvePointageEmployeeSelectValue,
} from './pointageListPageUtils';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';
import PointageFormModalShell, { POINTAGE_FORM_GRID_CLASS } from './components/PointageFormModalShell';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import { CustomDatePicker } from '../../shared/components/custom';
import {
  formatPointageDate,
  LEAVE_TYPE_VALUES,
  LEAVE_STATUS_VALUES,
  normalizeLeaveType,
  normalizeLeaveStatus,
  SAUDI_DEFAULT_TIMETRACKING_CONFIG,
} from './pointageHelpers';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';

/** Backend status values — English keys stored in DB */
const STATUS_VALUES = LEAVE_STATUS_VALUES;

const DemandesCongesPage = () => {
  const { t } = useTranslation();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const p = (key, opts) => t(`modules.pointage.pages.demandesCongesPage.${key}`, opts);

  const getStatusLabel = (status) => {
    const normalized = normalizeLeaveStatus(status);
    const map = {
      [STATUS_VALUES.pending]: p('status_pending'),
      [STATUS_VALUES.approved]: p('status_approved'),
      [STATUS_VALUES.rejected]: p('status_rejected'),
    };
    return map[normalized] || p('em_dash');
  };

  const getLeaveTypeLabel = (type) => {
    const normalized = normalizeLeaveType(type);
    const map = {
      [LEAVE_TYPE_VALUES.annual]: p('leave_annual'),
      [LEAVE_TYPE_VALUES.sick]: p('leave_sick'),
      [LEAVE_TYPE_VALUES.unpaid]: p('leave_unpaid'),
      [LEAVE_TYPE_VALUES.maternity]: p('leave_maternity'),
      [LEAVE_TYPE_VALUES.hajj]: p('leave_hajj'),
      [LEAVE_TYPE_VALUES.bereavement]: p('leave_bereavement'),
      [LEAVE_TYPE_VALUES.marriage]: p('leave_marriage'),
      [LEAVE_TYPE_VALUES.paternity]: p('leave_paternity'),
    };
    return map[normalized] || type;
  };

  const statusOptions = [
    { value: STATUS_VALUES.pending, labelKey: 'status_pending', icon: <Clock size={14} className="text-orange-400" />, style: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
    { value: STATUS_VALUES.approved, labelKey: 'status_approved', icon: <CheckCircle size={14} className="text-green-400" />, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: STATUS_VALUES.rejected, labelKey: 'status_rejected', icon: <XCircle size={14} className="text-red-400" />, style: 'bg-red-500/20 text-red-400 border-red-500/30' }
  ];

  const getStatusBadge = (status) => {
    const config = statusOptions.find(s => s.value === status) || statusOptions[0];
    return (
      <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${config.style}`}>
        {config.icon}
        {getStatusLabel(config.value)}
      </span>
    );
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [demandes, setDemandes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  const { employeeOptions } = usePointageEmployeeOptions(
    localApiAdapter.get.bind(localApiAdapter),
    { valueField: 'id' },
  );

  const cfgTheme = useModuleConfigTheme();
  const modalForm = getPointageModalFormClasses(cfgTheme.light);
  usePointageModalBodyLock(showModal, showViewModal);
  
  const [formTab, setFormTab] = useState('general');

  const CONGE_FORM_FIELD_COUNT = 12;

  const congeFormTabs = useMemo(() => buildPointageFormTabs({
    two: [
      { id: 'general', label: p('section_employee_info'), icon: User, iconClass: 'text-emerald-400' },
      { id: 'details', label: p('section_validation'), icon: CheckCircle, iconClass: 'text-green-400' },
    ],
  }, CONGE_FORM_FIELD_COUNT), [p]);
  
  const [formData, setFormData] = useState({ 
    employee_id: '',
    employee_name: '',
    leave_type: '',
    start_date: '',
    end_date: '',
    duration_days: '',
    status: '',
    request_date: '',
    reason: '',
    replacement_employee: '',
    validator_name: '',
    approval_date: '',
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('vue_generale');
  const [quickFilter, setQuickFilter] = useState('tous');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);
  const dateRangeRef = useRef(null);
  
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  useEffect(() => { loadDemandes(); }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) setIsDateRangeOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadDemandes = async () => {
    try {
      const response = await localApiAdapter.get('/pointage/conges');
      const rows = response?.data || response || [];
      setDemandes(sortRecordsNewestFirst(Array.isArray(rows) ? rows : []));
    } catch (error) {
      console.error('Error loading demandes:', error);
      setDemandes([]);
    }
  };

  const stats = {
    total: demandes.length,
    enAttente: demandes.filter(d => d.status === STATUS_VALUES.pending).length,
    approuvees: demandes.filter(d => d.status === STATUS_VALUES.approved).length,
    rejetees: demandes.filter(d => d.status === STATUS_VALUES.rejected).length,
    joursDisponibles: SAUDI_DEFAULT_TIMETRACKING_CONFIG.maxAnnualLeave
  };

  const mainTabs = [
    { id: 'vue_generale', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_statut', name: p('tab_by_status'), icon: Shield },
    { id: 'par_type', name: p('tab_by_type'), icon: Calendar }
  ];

  const quickFilterButtons = [
    { id: 'tous', name: p('filter_all'), icon: Layers },
    { id: STATUS_VALUES.pending, name: p('status_pending'), icon: Clock },
    { id: STATUS_VALUES.approved, name: p('status_approved_plural'), icon: CheckCircle },
    { id: STATUS_VALUES.rejected, name: p('status_rejected_plural'), icon: XCircle }
  ];

  const getTypeDistribution = () => [
    { distKey: 'annual', name: p('leave_annual'), count: demandes.filter(d => d.leave_type === LEAVE_TYPE_VALUES.annual).length, color: 'bg-gradient-to-r from-blue-500 to-blue-600', filterValue: LEAVE_TYPE_VALUES.annual },
    { distKey: 'sick', name: p('leave_sick'), count: demandes.filter(d => d.leave_type === LEAVE_TYPE_VALUES.sick).length, color: 'bg-gradient-to-r from-red-500 to-red-600', filterValue: LEAVE_TYPE_VALUES.sick },
    { distKey: 'unpaid', name: p('leave_unpaid'), count: demandes.filter(d => d.leave_type === LEAVE_TYPE_VALUES.unpaid).length, color: 'bg-gradient-to-r from-orange-500 to-orange-600', filterValue: LEAVE_TYPE_VALUES.unpaid },
    { distKey: 'maternity', name: p('leave_maternity'), count: demandes.filter(d => normalizeLeaveType(d.leave_type) === LEAVE_TYPE_VALUES.maternity).length, color: 'bg-gradient-to-r from-pink-500 to-pink-600', filterValue: LEAVE_TYPE_VALUES.maternity },
    { distKey: 'hajj', name: p('leave_hajj'), count: demandes.filter(d => normalizeLeaveType(d.leave_type) === LEAVE_TYPE_VALUES.hajj).length, color: 'bg-gradient-to-r from-purple-500 to-purple-600', filterValue: LEAVE_TYPE_VALUES.hajj },
    { distKey: 'bereavement', name: p('leave_bereavement'), count: demandes.filter(d => normalizeLeaveType(d.leave_type) === LEAVE_TYPE_VALUES.bereavement).length, color: 'bg-gradient-to-r from-gray-500 to-gray-600', filterValue: LEAVE_TYPE_VALUES.bereavement },
    { distKey: 'marriage', name: p('leave_marriage'), count: demandes.filter(d => normalizeLeaveType(d.leave_type) === LEAVE_TYPE_VALUES.marriage).length, color: 'bg-gradient-to-r from-rose-500 to-rose-600', filterValue: LEAVE_TYPE_VALUES.marriage },
    { distKey: 'paternity', name: p('leave_paternity'), count: demandes.filter(d => normalizeLeaveType(d.leave_type) === LEAVE_TYPE_VALUES.paternity).length, color: 'bg-gradient-to-r from-cyan-500 to-cyan-600', filterValue: LEAVE_TYPE_VALUES.paternity },
  ];

  const demandesDistribution = {
    vue_generale: {
      title: p('overview_requests'),
      data: [
        { distKey: 'pending', name: p('status_pending'), count: stats.enAttente, color: 'bg-gradient-to-r from-orange-500 to-orange-600', filterValue: STATUS_VALUES.pending },
        { distKey: 'approved', name: p('status_approved_plural'), count: stats.approuvees, color: 'bg-gradient-to-r from-green-500 to-green-600', filterValue: STATUS_VALUES.approved },
        { distKey: 'rejected', name: p('status_rejected_plural'), count: stats.rejetees, color: 'bg-gradient-to-r from-red-500 to-red-600', filterValue: STATUS_VALUES.rejected },
        { distKey: 'total', name: p('total'), count: stats.total, color: 'bg-gradient-to-r from-blue-500 to-blue-600', filterValue: null }
      ]
    },
    par_statut: {
      title: p('distribution_by_status'),
      data: [
        { distKey: 'pending', name: p('status_pending'), count: stats.enAttente, color: 'bg-gradient-to-r from-orange-500 to-orange-600', filterValue: STATUS_VALUES.pending },
        { distKey: 'approved', name: p('status_approved_plural'), count: stats.approuvees, color: 'bg-gradient-to-r from-green-500 to-green-600', filterValue: STATUS_VALUES.approved },
        { distKey: 'rejected', name: p('status_rejected_plural'), count: stats.rejetees, color: 'bg-gradient-to-r from-red-500 to-red-600', filterValue: STATUS_VALUES.rejected }
      ]
    },
    par_type: {
      title: p('distribution_by_type'),
      data: getTypeDistribution()
    }
  };

  const distributionInfo = demandesDistribution[mainViewMode] || demandesDistribution.vue_generale;

  
  const baseFilteredDemandes = useMemo(() => demandes.filter(d => {
    if (statusFilter !== 'all' && d.status !== statusFilter) return false;
    
    if (dateRange.start || dateRange.end) {
      const requestDate = new Date(d.request_date);
      
      if (dateRange.start) {
        const startOfDay = new Date(dateRange.start);
        startOfDay.setHours(0, 0, 0, 0);
        if (requestDate < startOfDay) return false;
      }
      
      if (dateRange.end) {
        const endOfDay = new Date(dateRange.end);
        endOfDay.setHours(23, 59, 59, 999);
        if (requestDate > endOfDay) return false;
      }
    }
    
    if (searchTerm && !d.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) && !d.leave_type?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    if (quickFilter !== 'tous' && d.status !== quickFilter) return false;
    
    if (activeDistributionFilter && activeDistributionFilter !== 'total') {
      const distItem = distributionInfo.data.find((item) => item.distKey === activeDistributionFilter);
      if (distItem && distItem.filterValue != null) {
        if (mainViewMode === 'par_type') {
          if (d.leave_type !== distItem.filterValue) return false;
        } else if (d.status !== distItem.filterValue) {
          return false;
        }
      }
    }
    return true;
  }), [demandes, statusFilter, dateRange, searchTerm, mainViewMode, quickFilter, activeDistributionFilter]);

  const columnFilterRules = useMemo(() => [
      textColumnRule('request_date', (d) => formatPointageDate(d.request_date) || ''),
      textColumnRule('employee_name', (d) => d.employee_name || ''),
      textColumnRule('leave_type', (d) => getLeaveTypeLabel(d.leave_type)),
      textColumnRule('start_date', (d) => formatPointageDate(d.start_date) || ''),
      textColumnRule('duration_days', (d) => String(d.duration_days ?? '')),
      exactColumnRule('status', (d) => normalizeLeaveStatus(d.status)),
    ], [getLeaveTypeLabel, getStatusLabel]);

  const filteredDemandes = useMemo(
    () => applyColumnFilters(baseFilteredDemandes, colFilters, columnFilterRules),
    [baseFilteredDemandes, colFilters, columnFilterRules],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        employee_name: formData.employee_name
          || resolvePointageEmployeeDisplayName(formData.employee_id, employeeOptions, ''),
      };
      if (editingRecord) {
        await localApiAdapter.put(`/pointage/conges/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, p('title'));
      } else {
        await localApiAdapter.post('/pointage/conges', submitData);
        notifyCreated(showNotify, p('title'));
      }
    } catch (error) {
      console.error('Error saving:', error);
      alert(p('alert_save_error'));
    } finally {
      resetForm();
      loadDemandes();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ ...record });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('confirm_delete'))) {
      try {
        await localApiAdapter.delete(`/pointage/conges/${id}`);
        notifyDeleted(showNotify, p('title'));
        loadDemandes();
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await localApiAdapter.put(`/pointage/conges/${id}`, { status: newStatus });
      loadDemandes();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const clickableStatusOptions = useMemo(() => [
    { value: STATUS_VALUES.pending, label: p('status_pending'), icon: Clock, style: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
    { value: STATUS_VALUES.approved, label: p('status_approved'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: STATUS_VALUES.rejected, label: p('status_rejected'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
  ], [p]);

  const statusColumnFilterOptions = useMemo(
    () => clickableStatusOptions.map(({ value, label }) => ({ value, label })),
    [clickableStatusOptions],
  );

  const handleBulkDelete = async () => {
    if (window.confirm(p('confirm_bulk_delete', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/pointage/conges/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, p('title'));
        setSelectedRecords([]);
        loadDemandes();
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      employee_id: '', employee_name: '', leave_type: '', start_date: '', end_date: '', duration_days: '',
      status: '', request_date: '', reason: '', replacement_employee: '', validator_name: '',
      approval_date: '', notes: ''
    });
    setEditingRecord(null);
    setFormTab('general');
    setShowModal(false);
  };

  const {
    selectedRecords,
    setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
  } = useQawaemFilteredRowSelection(filteredDemandes);


  const kpiCards = [
    { title: p('kpi_total'), value: stats.total, icon: FileText, color: 'from-blue-500 to-blue-600' },
    { title: p('kpi_pending'), value: stats.enAttente, icon: Clock, color: 'from-orange-500 to-orange-600' },
    { title: p('kpi_approved'), value: stats.approuvees, icon: CheckCircle, color: 'from-green-500 to-green-600' },
    { title: p('kpi_rejected'), value: stats.rejetees, icon: XCircle, color: 'from-red-500 to-red-600' },
    { title: p('kpi_days_available'), value: stats.joursDisponibles, icon: Calendar, color: 'from-purple-500 to-purple-600' }
  ];

  const leaveTypeFormOptions = [
    { value: LEAVE_TYPE_VALUES.annual, label: p('leave_annual') },
    { value: LEAVE_TYPE_VALUES.sick, label: p('leave_sick') },
    { value: LEAVE_TYPE_VALUES.unpaid, label: p('leave_unpaid') },
    { value: LEAVE_TYPE_VALUES.maternity, label: p('leave_maternity') },
    { value: LEAVE_TYPE_VALUES.hajj, label: p('leave_hajj') },
    { value: LEAVE_TYPE_VALUES.bereavement, label: p('leave_bereavement') },
    { value: LEAVE_TYPE_VALUES.marriage, label: p('leave_marriage') },
    { value: LEAVE_TYPE_VALUES.paternity, label: p('leave_paternity') },
  ];

  const POINTAGE_TABLE_COLUMNS = [
    { key: 'request_date', labelKey: 'col_request_date' },
    { key: 'employee_name', labelKey: 'col_employee' },
    { key: 'leave_type', labelKey: 'col_leave_type' },
    { key: 'start_date', labelKey: 'col_start_date' },
    { key: 'duration_days', labelKey: 'col_duration' },
    { key: 'status', labelKey: 'col_status' },
    { key: 'actions', label: t('common.actions'), required: true },
  ];
  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildPointageColumnVisibilityState(POINTAGE_TABLE_COLUMNS),
  );
  const qawaemColumnDefs = useMemo(
    () => buildPointageQawaemColumnDefs(POINTAGE_TABLE_COLUMNS, p, columnVisibility),
    [p, t, columnVisibility],
  );
  const handleToggleColumn = createPointageHandleToggleColumn(setColumnVisibility, POINTAGE_TABLE_COLUMNS);
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isPointageColumnVisible(columnVisibility, key);
  const tableColSpan = pointageTableColSpan(columnVisibility);

  return (
    <div className={`space-y-3 employees-container`}>
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Calendar className="h-8 w-8 text-blue-400" />
            {p('title')}
            <button onClick={loadDemandes} className="refresh-button" title={t('common.refresh')}><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3">
          {buildPointageBulkDeleteFilterAction({
            count: selectedRecords.length,
            onDelete: handleBulkDelete,
            label: `${t('common.delete')} (${selectedRecords.length})`,
            title: t('common.delete', { defaultValue: 'حذف' }),
          })}
          <button onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}>
            <Filter className="h-5 w-5" /><span className="hidden sm:inline">{t('common.advanced_options')}</span>{showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2" onClick={() => { setEditingRecord(null); setShowModal(true); }}>
            <Plus className="h-5 w-5" /><span>{p('new_request')}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpiCards.slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div><p className="text-white/60 text-sm font-medium">{stat.title}</p><p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p></div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('tous'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {quickFilterButtons.map((filter) => (
                <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                </button>
              ))}
            </div>
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>{distributionInfo.data.map((item) => (
                <button key={item.distKey} onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.distKey ? null : item.distKey)} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === item.distKey ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.distKey ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }} transition={{ duration: 0.6, delay: 0.1 }} className={`h-3 rounded-full ${item.color}`}></motion.div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === item.distKey ? 'text-white/80' : 'text-white/60'}`}>{p('percent_of_total', { percent: (() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })() })}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                    selectAllTitle={t('common.select_all', { defaultValue: 'تحديد الكل' })}
                    deselectAllTitle={t('common.deselect_all', { defaultValue: 'إلغاء تحديد الكل' })}
                  />
                  {isColVisible('request_date') && (
                    <TableColumnHeaderFilter columnKey="request_date" label={p('col_request_date')} value={colFilters.request_date ?? ''} onChange={(v) => setColFilter('request_date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('employee_name') && (
                    <TableColumnHeaderFilter columnKey="employee_name" label={p('col_employee')} value={colFilters.employee_name ?? ''} onChange={(v) => setColFilter('employee_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('leave_type') && (
                    <TableColumnHeaderFilter columnKey="leave_type" label={p('col_leave_type')} value={colFilters.leave_type ?? ''} onChange={(v) => setColFilter('leave_type', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('start_date') && (
                    <TableColumnHeaderFilter columnKey="start_date" label={p('col_start_date')} value={colFilters.start_date ?? ''} onChange={(v) => setColFilter('start_date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('duration_days') && (
                    <TableColumnHeaderFilter columnKey="duration_days" label={p('col_duration')} value={colFilters.duration_days ?? ''} onChange={(v) => setColFilter('duration_days', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  {isColVisible('status') && (
                    <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={statusColumnFilterOptions} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  <QawaemTableHeaderActionsCell
                    label={t('common.actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName={POINTAGE_ACTIONS_TH_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredDemandes.length > 0 ? (
                  filteredDemandes.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <QawaemTableSelectRowCell
                        checked={selectedRecords.includes(record.id)}
                        onChange={() => handleSelectRecord(record.id)}
                      />
                      {isColVisible('request_date') && (
                        <td className="px-6 py-4"><span className="text-white font-semibold">{record.request_date ? formatPointageDate(record.request_date) : '-'}</span></td>
                      )}
                      {isColVisible('employee_name') && (
                        <td className="px-6 py-4"><div className="flex flex-col"><span className="text-white font-semibold">{resolvePointageEmployeeDisplayName(record.employee_id, employeeOptions, record.employee_name || '-')}</span></div></td>
                      )}
                      {isColVisible('leave_type') && (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{getLeaveTypeLabel(record.leave_type) || '-'}</span></td>
                      )}
                      {isColVisible('start_date') && (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{record.start_date ? formatPointageDate(record.start_date) : '-'}</span></td>
                      )}
                      {isColVisible('duration_days') && (
                        <td className="px-6 py-4 text-center"><span className="text-white font-bold text-lg">{p('duration_days_short', { count: record.duration_days || 0 })}</span></td>
                      )}
                      {isColVisible('status') && (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={normalizeLeaveStatus(record.status)}
                              recordId={record.id}
                              options={clickableStatusOptions}
                              onChange={handleStatusChange}
                              title={p('click_change_status', { defaultValue: t('common.change_status') })}
                            />
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 text-center align-middle">
                        <div className="flex justify-center">
                          <QawaemErpRowActions
                            onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                            onEdit={() => handleEdit(record)}
                            onDelete={() => handleDelete(record.id)}
                            viewTitle={t('common.view')}
                            editTitle={t('common.edit')}
                            deleteTitle={t('common.delete')}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><Calendar size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={tableColSpan} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm">
                      <span>{p('footer_total', { count: filteredDemandes.length })}</span>
                      <span>{p('footer_pending')} <span className="text-white font-semibold">{filteredDemandes.filter(d => d.status === STATUS_VALUES.pending).length}</span></span>
                      <span>{p('footer_approved')} <span className="text-white font-semibold">{filteredDemandes.filter(d => d.status === STATUS_VALUES.approved).length}</span></span>
                      <span>{p('footer_rejected')} <span className="text-white font-semibold">{filteredDemandes.filter(d => d.status === STATUS_VALUES.rejected).length}</span></span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredDemandes.length > 0 ? (
              filteredDemandes.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><User size={20} className="text-emerald-400" />{record.employee_name || p('na')}</h3>
                        <div className="flex">{getStatusBadge(record.status)}</div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_employee_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_employee')}</span> {record.employee_name || '-'}</div>
                            <div><span className="font-medium">{p('label_request_date')}</span> {record.request_date ? formatPointageDate(record.request_date) : '-'}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_period_duration')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_start_date')}</span> {record.start_date ? formatPointageDate(record.start_date) : '-'}</div>
                            <div><span className="font-medium">{p('label_end_date')}</span> {record.end_date ? formatPointageDate(record.end_date) : '-'}</div>
                            <div><span className="font-medium">{p('label_duration')}</span> <span className="text-white font-bold">{p('duration_days', { count: record.duration_days || 0 })}</span></div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_type_reason')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_type')}</span> {getLeaveTypeLabel(record.leave_type) || '-'}</div>
                            <div><span className="font-medium">{p('label_reason')}</span> {record.reason || p('not_specified')}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_validation')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_validator')}</span> {record.validator_name || p('pending_validator')}</div>
                            <div><span className="font-medium">{p('label_approval_date')}</span> {record.approval_date ? formatPointageDate(record.approval_date) : '-'}</div>
                            {record.replacement_employee && <div><span className="font-medium">{p('label_replacement')}</span> {record.replacement_employee}</div>}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 ml-4 shrink-0">
                      <QawaemErpRowActions
                        compact
                        size={14}
                        onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                        onEdit={() => handleEdit(record)}
                        onDelete={() => handleDelete(record.id)}
                        viewTitle={t('common.view')}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><Calendar size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty')}</p></div>
            )}
          </div>
        )}
      </div>

      {showViewModal && viewingRecord && (
        <QawaemErpViewModal
          open={showViewModal}
          onClose={() => setShowViewModal(false)}
          title={p('view_modal_title')}
          icon={Eye}
          maxWidth="1100px"
          testId="demandes-conges-view-modal"
        >
          <div className="space-y-4">
                <div className={modalForm.viewCard}>
                  <h3 className={`${modalForm.viewHeading} flex items-center gap-2`}><User size={18} className="text-emerald-400" />{p('section_employee_info')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><p className={modalForm.viewLabel}>{p('col_employee')}</p><p className={modalForm.viewValue}>{viewingRecord.employee_name || '-'}</p></div>
                    <div><p className={modalForm.viewLabel}>{p('col_request_date')}</p><p className={modalForm.viewValue}>{viewingRecord.request_date ? formatPointageDate(viewingRecord.request_date) : '-'}</p></div>
                    <div><p className={modalForm.viewLabel}>{t('common.status')}</p>{getStatusBadge(viewingRecord.status)}</div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={modalForm.viewCard}>
                    <h3 className={modalForm.viewHeading}>{p('section_period_duration')}</h3>
                    <div className="space-y-3">
                      <div><p className={modalForm.viewLabel}>{p('col_start_date')}</p><p className={modalForm.viewValue}>{viewingRecord.start_date ? formatPointageDate(viewingRecord.start_date) : '-'}</p></div>
                      <div><p className={modalForm.viewLabel}>{p('date_end_label')}</p><p className={modalForm.viewValue}>{viewingRecord.end_date ? formatPointageDate(viewingRecord.end_date) : '-'}</p></div>
                      <div><p className={modalForm.viewLabel}>{p('col_duration')}</p><p className="text-white font-bold text-xl">{p('duration_days', { count: viewingRecord.duration_days || 0 })}</p></div>
                    </div>
                  </div>
                  <div className={modalForm.viewCard}>
                    <h3 className={modalForm.viewHeading}>{p('section_type_reason')}</h3>
                    <div className="space-y-3">
                      <div><p className={modalForm.viewLabel}>{p('leave_type_label')}</p><p className={modalForm.viewValue}>{getLeaveTypeLabel(viewingRecord.leave_type) || '-'}</p></div>
                      <div><p className={modalForm.viewLabel}>{p('label_reason')}</p><p className={modalForm.viewValue}>{viewingRecord.reason || p('not_specified')}</p></div>
                      {viewingRecord.notes && <div><p className={modalForm.viewLabel}>{p('notes_label')}</p><p className="text-white/70 text-sm">{viewingRecord.notes}</p></div>}
                    </div>
                  </div>
                </div>
                <div className={modalForm.viewCard}>
                  <h3 className={modalForm.viewHeading}>{p('section_validation')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><p className={modalForm.viewLabel}>{p('validator')}</p><p className={modalForm.viewValue}>{viewingRecord.validator_name || p('pending_validator')}</p></div>
                    <div><p className={modalForm.viewLabel}>{p('approval_date')}</p><p className={modalForm.viewValue}>{viewingRecord.approval_date ? formatPointageDate(viewingRecord.approval_date) : '-'}</p></div>
                    <div><p className={modalForm.viewLabel}>{p('replacement')}</p><p className={modalForm.viewValue}>{viewingRecord.replacement_employee || p('not_specified')}</p></div>
                  </div>
                </div>
              </div>
        </QawaemErpViewModal>
      )}

      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={resetForm}
          title={editingRecord ? p('modal_edit') : p('modal_new')}
          icon={Calendar}
          onSubmit
          formId="conge-form"
          saveLabel={editingRecord ? p('update') : p('create')}
          maxWidth="1200px"
          testId="demandes-conges-crud-modal"
        >
          <PointageFormModalShell
            isLight={cfgTheme.light}
            tabs={congeFormTabs}
            activeTab={formTab}
            onTabChange={setFormTab}
            formId="conge-form"
            onSubmit={handleSubmit}
            ariaLabel={editingRecord ? p('modal_edit') : p('modal_new')}
          >
            {pointageFormTabVisible(congeFormTabs, formTab, 'general') && (
              <div className="space-y-4">
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div><label className={modalForm.label}>{<RequiredLabelText text={p('employee_required')} />}</label><CustomSelect value={resolvePointageEmployeeSelectValue(formData, employeeOptions)} onChange={(value) => { const { employeeId, employeeName } = resolvePointageEmployeeFromSelect(value, employeeOptions); setFormData({ ...formData, employee_id: employeeId, employee_name: employeeName }); }} options={employeeOptions} placeholder={p('enter_name')} searchable={true} creatable={false} autoSelect={false} /></div>
                <div><label className={modalForm.label}>{<RequiredLabelText text={p('request_date_required')} />}</label><CustomDatePicker value={formData.request_date} onChange={(value) => setFormData({ ...formData, request_date: value })} placeholder={p('select')} /></div>
              </div>
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div><label className={modalForm.label}>{<RequiredLabelText text={p('start_date_required')} />}</label><CustomDatePicker value={formData.start_date} onChange={(value) => setFormData({ ...formData, start_date: value })} placeholder={p('select')} /></div>
                <div><label className={modalForm.label}>{<RequiredLabelText text={p('end_date_required')} />}</label><CustomDatePicker value={formData.end_date} onChange={(value) => setFormData({ ...formData, end_date: value })} placeholder={p('select')} /></div>
                <div><label className={modalForm.label}>{<RequiredLabelText text={p('duration_required')} />}</label><input type="number" required value={formData.duration_days} onChange={(e) => setFormData({ ...formData, duration_days: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('duration_placeholder')} /></div>
              </div>
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div><label className={modalForm.label}>{p('leave_type_label')} <span className="qawaem-required-mark">*</span></label><CustomSelect value={formData.leave_type} onChange={(value) => setFormData({ ...formData, leave_type: value })} options={leaveTypeFormOptions} placeholder={p('select')} searchable={true} creatable={true} autoSelect={false} /></div>
                <div><label className={modalForm.label}>{<RequiredLabelText text={p('status_required')} />}</label><CustomSelect value={formData.status} onChange={(value) => setFormData({ ...formData, status: value })} options={statusOptions.map(s => ({ value: s.value, label: getStatusLabel(s.value), icon: s.icon }))} placeholder={p('select')} searchable={true} creatable={true} autoSelect={false} /></div>
                <div className="md:col-span-3"><label className={modalForm.label}>{p('reason_label')}</label><textarea value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} rows={3} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('reason_placeholder')} /></div>
                <div className="md:col-span-3"><label className={modalForm.label}>{p('notes_label')}</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('notes_placeholder')} /></div>
              </div>
              </div>
            )}
            {pointageFormTabVisible(congeFormTabs, formTab, 'details') && (
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div><label className={modalForm.label}>{p('validator')}</label><CustomSelect value={formData.validator_name} onChange={(value) => setFormData({ ...formData, validator_name: value })} options={employeeOptions} placeholder={p('enter_name')} searchable={true} creatable={true} autoSelect={false} /></div>
                <div><label className={modalForm.label}>{p('approval_date')}</label><CustomDatePicker value={formData.approval_date} onChange={(value) => setFormData({ ...formData, approval_date: value })} placeholder={p('select')} /></div>
                <div><label className={modalForm.label}>{p('replacement')}</label><CustomSelect value={formData.replacement_employee} onChange={(value) => setFormData({ ...formData, replacement_employee: value })} options={employeeOptions} placeholder={p('enter_name')} searchable={true} creatable={true} autoSelect={false} /></div>
              </div>
            )}
          </PointageFormModalShell>
        </QawaemErpCrudModal>
      )}
      <PointageCrudNotificationToast toast={notifyToast} onDismiss={dismissNotify} />
    </div>
  );
};

export default DemandesCongesPage;

